import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import usePrevious from '@e3dc-react/shell/Hooks/usePrevious'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { isUndefined } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useFarmContext } from 'src/Elements/Farm'
import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'
import {
  getFarmPowerMetersByStorage,
  getPowerMeter,
  getPowerMeterListByStorage,
  getPowerMetersByStorage,
  updatePowerMetersSettings,
} from '../api/power-meters.api'
import { PowerMeterUpdateDto } from '../dto'
import { PowerMeterModel } from '../models'
import { PowermeterHardwareModel } from '../models/power-meter.model'
import { PowerMeterProviderContext } from '../providers/PowerMeterProvider/context'
import { PMType, SettingsFormValues } from '../types'

interface UsePowerMetersHook {
  /**
   * Load all the settings for the Power Meter connected to the initial given storage-reference.
   */
  loadAllForStorage: (abortSignal?: AbortSignal) => Promise<void>

  /**
   * Updates the settings for the referenced Power Meter. These changes happen directly to the referenced, connected device
   * @param storageSerialNr
   * @param resourceId
   * @param values
   */
  updateSettings: (storageSerialNr: string, resourceId: string, values: SettingsFormValues, powerMeterType: PMType) => Promise<void>

  /**
   * All the loaded Power Meter settings related to the initial provided storage reference
   */
  powerMeters: PowerMeterModel[]

  /**
   * Constructs the offical label for a Power Meter settings. Used in the navigation tabs.
   *
   * There are special cases to consider for this, as the first Power Meter always has its own fixed label which differs
   * from its given name.
   *
   * @param item
   */
  getDisplayName: (item: PowerMeterModel) => string
  error?: Error | AuthFetchError
  isLoading: boolean
}

const DISPLAY_NAME_BY_TYPES: PMType[] = [PMType.PM_TYPE_ROOT, PMType.PM_TYPE_FARM]

/**
 * The hook provides tools to load and update the Power Meter Settings for a single, initial provided storage-reference.
 *
 * When calling or, after updating Power Meter Settings, the up-to-date settings will be cached and provided through the
 * exposed `powerMeters`-property.
 *
 * @param storageSerialNo
 */
const usePowerMeters = (storageSerialNo?: string): UsePowerMetersHook => {
  const contextValue = React.useContext(PowerMeterProviderContext)
  const [error, setError] = useState<Error | AuthFetchError>()
  const { t } = useCustomTranslation()
  const prevStorageSerialNo = usePrevious(storageSerialNo)
  const { isFarmMasterSelected } = useFarmContext()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  if (contextValue === undefined) {
    throw new Error('usePowerMeters must be used within BatteryProvider')
  }
  const getDisplayName = (item: PowerMeterModel): string => {
    if (DISPLAY_NAME_BY_TYPES.includes(item.conditionType)) {
      return t(`systemSettings.powerMeter.deviceDisplayNames.type${item.conditionType}`)
    }
    return item.deviceName || '-'
  }

  const { values, setValues } = contextValue
  const authFetch = useAuthFetchWithResponse()

  const valuesByStorage = useMemo<PowerMeterModel[]>(() => {
    if (isUndefined(storageSerialNo)) {
      return []
    }
    return values.get(storageSerialNo) || []
  }, [storageSerialNo, values])

  useEffect(() => {
    setError(undefined)
    if (storageSerialNo !== prevStorageSerialNo) {
      loadAllForStorage()
    }
  }, [storageSerialNo]) // eslint-disable-line

  const loadAllForStorage = async (abortSignal?: AbortSignal): Promise<void> => {
    setIsLoading(true)
    if (isUndefined(storageSerialNo)) {
      return
    }
    const referencesResult = isFarmMasterSelected
      ? await getFarmPowerMetersByStorage(storageSerialNo, authFetch, abortSignal)
      : await getPowerMetersByStorage(storageSerialNo, authFetch, abortSignal)

    if (referencesResult.hasErrors) {
      setError(referencesResult.error)
    }
    const promises = (referencesResult.value ?? []).map(item => getPowerMeter(item.storageSerialNo, item.powerMeterIndex, authFetch))
    const results = await Promise.all(promises)
    const powerMeters = results.filter(result => !result.hasErrors).map(result => result.value) as PowerMeterModel[]
    const powerMetersDecorated = (referencesResult.value ?? []).map((powerMeter, index) => ({
      ...powerMeters[index],
      storageSerialNo: powerMeter.storageSerialNo,
    }))
    setValues(storageSerialNo, powerMetersDecorated)
    setIsLoading(false)
  }

  const updateSettings = async (storageSerialNo: string, resourceId: string, values: SettingsFormValues, powerMeterType: PMType): Promise<void> => {
    if (isUndefined(storageSerialNo) || isUndefined(resourceId)) {
      throw new Error(`Invalid activeStorageId or tabIndex (id: ${storageSerialNo}, tabIndex: ${resourceId})`)
    }

    let dto: PowerMeterUpdateDto

    if (powerMeterType !== PMType.PM_TYPE_ROOT) {
      dto = {
        deviceName: values.deviceName,
      }
    } else {
      dto = {
        deviceName: values.deviceName,
        phaseBalanced: values.balancedPhases,
        phaseElemination: values.phaseElimination || [false, false, false],
      }
    }

    await updatePowerMetersSettings(storageSerialNo, resourceId, dto, authFetch)
  }

  return { loadAllForStorage, updateSettings, powerMeters: valuesByStorage, getDisplayName, error, isLoading }
}

export const usePowermeterList = (storageId?: string): [PowermeterHardwareModel[] | null, boolean, boolean] => {
  const authFetch = useAuthFetchWithResponse()

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: ['powermeters', storageId],
    queryFn: () => getPowerMeterListByStorage(storageId, authFetch),
    enabled: !!storageId,
  })

  return [data || null, isLoading, isError]
}

export default usePowerMeters
